import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-button text-primary-foreground hover:shadow-glow-sm hover:-translate-y-0.5 active:translate-y-0",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-primary/10 hover:border-primary hover:text-primary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-primary/10 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        vestn:
          "bg-gradient-button text-primary-foreground font-semibold hover:shadow-glow-sm hover:-translate-y-0.5 active:translate-y-0",
        "vestn-outline":
          "border-2 border-primary/40 bg-transparent text-primary hover:bg-primary/15 hover:border-primary hover:shadow-glow-sm font-medium",
        "vestn-ghost":
          "text-muted-foreground hover:text-primary hover:bg-primary/10",
        hero: "bg-gradient-button text-primary-foreground font-semibold text-base px-8 py-4 hover:shadow-glow hover:-translate-y-0.5 active:translate-y-0",
        "hero-outline":
          "border-2 border-primary/50 bg-transparent text-primary font-semibold text-base px-8 py-4 hover:bg-primary/15 hover:border-primary hover:shadow-glow",
        card: "bg-gradient-button text-primary-foreground font-medium flex-1 hover:shadow-glow-sm hover:-translate-y-0.5 active:translate-y-0",
        "card-outline":
          "border border-border bg-transparent text-foreground font-medium flex-1 hover:bg-primary/10 hover:border-primary hover:text-primary",
        wallet:
          "bg-card border border-border text-foreground font-medium hover:border-primary/50 hover:bg-primary/5",
        "wallet-connected":
          "bg-card border border-primary/30 text-foreground font-medium hover:border-primary/50",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-lg px-8",
        xl: "h-12 rounded-xl px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
